import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "wouter";

import {
  LiabilityInterestRateHistory,
  LiabilityPageProps,
  LiabilityPaymentHistoryEntry,
  LiabilityUpdateObject,
} from "../types";
import { notify } from "../../../components/Notification";
import {
  Wrapper,
  SectionWrapper,
  SkeletonBox,
  HeroSkeleton,
  HeroPanel,
  HeroActionsRow,
  CardSkeleton,
  RowBetween,
  StatGridSkeleton,
  StatCellSkeleton,
  DetailsGridSkeleton,
  TrendsHeaderSkeleton,
  TitleBlockSkeleton,
  ChartAreaSkeleton,
  TableCardSkeleton,
  TableHeaderSkeleton,
  TableRowSkeleton,
} from "./styles";
import Button, { ButtonTypes } from "../../../components/Button";
import { UpdateLiability } from "../panels/updateLiability";
import { PaymentPanel, SetInterestRate } from "../panels";
import { SetInterestRateMode } from "../panels/setInterestRate";
import {
  ErrorResponse,
  useDeleteInterestRateMutation,
} from "../../../services";
import { getErrorMessages } from "../../../services/errors";
import { DropdownAction } from "../../../components/Dropdown/types";
import { EmptyState, ErrorState } from "../../../components/States";
import {
  LiabilityHero,
  LoanBreakdown,
  LiabilityTrends,
  InterestRateHistory,
  PaymentHistory,
  DeleteLiabilityModal,
  DeleteInterestRateModal,
  DeletePaymentModal,
  StatusChangeModal,
} from "./components";
import {
  useLiabilityData,
  useLiabilityActions,
  useLiabilityBalanceHistory,
  useLiabilityModals,
  usePaymentPanel,
} from "./hooks";

// Matched to the real sections so the skeleton and loaded layout take the same height.
const SKELETON_STAT_CELLS = [0, 1, 2, 3];
const SKELETON_DETAIL_CELLS = [0, 1, 2, 3, 4, 5];
const SKELETON_TABLE_ROWS = [0, 1, 2, 3];

// A flush table card standing in for InterestRateHistory / PaymentHistory.
const TableSectionSkeleton = () => (
  <TableCardSkeleton>
    <TableHeaderSkeleton>
      <SkeletonBox width="180px" height="22px" />
      <SkeletonBox width="240px" height="14px" />
    </TableHeaderSkeleton>
    {SKELETON_TABLE_ROWS.map((row) => (
      <TableRowSkeleton key={row}>
        <SkeletonBox width="30%" height="16px" />
        <SkeletonBox width="18%" height="16px" />
      </TableRowSkeleton>
    ))}
  </TableCardSkeleton>
);

// Mirrors the real section stack so content loads in without a layout shift.
const LiabilitySkeleton = () => (
  <Wrapper aria-busy="true" aria-label="Loading liability details">
    <SectionWrapper $isFirst>
      <HeroSkeleton>
        <HeroPanel>
          <SkeletonBox width="45%" height="28px" />
          <SkeletonBox width="30%" height="16px" />
          <SkeletonBox width="55%" height="44px" />
          <SkeletonBox width="35%" height="16px" />
          <StatGridSkeleton>
            {SKELETON_STAT_CELLS.map((cell) => (
              <StatCellSkeleton key={cell}>
                <SkeletonBox width="70%" height="12px" />
                <SkeletonBox width="55%" height="18px" />
              </StatCellSkeleton>
            ))}
          </StatGridSkeleton>
        </HeroPanel>
        <HeroPanel $divided>
          <HeroActionsRow>
            <SkeletonBox width="44px" height="40px" />
            <SkeletonBox width="150px" height="40px" />
          </HeroActionsRow>
          <SkeletonBox width="40%" height="12px" />
          <SkeletonBox width="60%" height="28px" />
          <SkeletonBox width="100%" height="10px" />
          <SkeletonBox width="100%" height="44px" />
        </HeroPanel>
      </HeroSkeleton>
    </SectionWrapper>

    <SectionWrapper>
      <CardSkeleton>
        <RowBetween>
          <SkeletonBox width="160px" height="22px" />
          <SkeletonBox width="90px" height="24px" />
        </RowBetween>
        <SkeletonBox width="100%" height="10px" />
        <StatGridSkeleton>
          {SKELETON_STAT_CELLS.map((cell) => (
            <StatCellSkeleton key={cell}>
              <SkeletonBox width="60%" height="12px" />
              <SkeletonBox width="75%" height="18px" />
            </StatCellSkeleton>
          ))}
        </StatGridSkeleton>
        <SkeletonBox width="85%" height="14px" />
        <DetailsGridSkeleton>
          {SKELETON_DETAIL_CELLS.map((cell) => (
            <StatCellSkeleton key={cell}>
              <SkeletonBox width="50%" height="12px" />
              <SkeletonBox width="70%" height="16px" />
            </StatCellSkeleton>
          ))}
        </DetailsGridSkeleton>
      </CardSkeleton>
    </SectionWrapper>

    <SectionWrapper>
      <CardSkeleton>
        <TrendsHeaderSkeleton>
          <TitleBlockSkeleton>
            <SkeletonBox width="160px" height="22px" />
            <SkeletonBox width="220px" height="14px" />
          </TitleBlockSkeleton>
          <SkeletonBox width="200px" height="36px" />
        </TrendsHeaderSkeleton>
        <ChartAreaSkeleton />
      </CardSkeleton>
    </SectionWrapper>

    <SectionWrapper>
      <TableSectionSkeleton />
    </SectionWrapper>

    <SectionWrapper>
      <TableSectionSkeleton />
    </SectionWrapper>
  </Wrapper>
);

export const LiabilityPage: React.FC<LiabilityPageProps> = ({ id }) => {
  const [, setLocation] = useLocation();
  const [liabilityUpdate, setLiabilityUpdate] = useState(
    {} as LiabilityUpdateObject,
  );
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [rateMode, setRateMode] = useState<SetInterestRateMode | null>(null);
  const [selectedRate, setSelectedRate] = useState<
    LiabilityInterestRateHistory | undefined
  >();
  const [rateToDelete, setRateToDelete] =
    useState<LiabilityInterestRateHistory | null>(null);
  const [rateDeleteError, setRateDeleteError] = useState<string | null>(null);

  const [deleteInterestRateTrigger, { isLoading: isDeletingRate }] =
    useDeleteInterestRateMutation();

  const openRatePanel = useCallback(
    (mode: SetInterestRateMode, rate?: LiabilityInterestRateHistory) => {
      setSelectedRate(rate);
      setRateMode(mode);
    },
    [],
  );

  const closeRatePanel = useCallback(() => {
    setRateMode(null);
    setSelectedRate(undefined);
  }, []);

  const {
    liability,
    isLoading,
    error,
    notFoundError,
    lastPaymentDate,
    refetch,
  } = useLiabilityData(id);

  const {
    activateModal,
    setActivateModal,
    isDeleteOpen,
    setIsDeleteOpen,
    deletePayment,
    setDeletePayment,
  } = useLiabilityModals();

  const { panel, setPanel, openPanel, closePanel, getPanelTitle } =
    usePaymentPanel();

  const {
    points: balancePoints,
    payments: paymentHistory,
    isLoading: isHistoryLoading,
  } = useLiabilityBalanceHistory(id);

  const actions = useLiabilityActions({
    id,
    liability,
    setActivateModal,
    setIsUpdateOpen,
    setIsDeleteOpen,
    setLiabilityUpdate,
    setDeletePayment,
    setPanel,
    panel,
    deletePayment,
  });

  // Memoize dropdown actions to prevent unnecessary re-renders
  const dropdownActions = useMemo<DropdownAction[]>(
    () => [
      {
        id: "edit",
        label: "Edit liability",
        icon: "",
        onClick: actions.handleEdit,
        show: true,
      },
      {
        id: "set-rate",
        label: "Set interest rate",
        icon: "",
        onClick: () => openRatePanel("set"),
        show: true,
      },
      {
        id: "deactivate",
        label: "Deactivate",
        icon: "",
        onClick: () => setActivateModal(true),
        show: liability.isActive,
      },
      {
        id: "activate",
        label: "Reactivate",
        icon: "",
        onClick: () => setActivateModal(true),
        show: !liability.isActive,
      },
      {
        id: "delete",
        label: "Delete liability",
        icon: "",
        onClick: () => {
          void (async () => {
            await actions.fetchDeletionImpact();
            actions.handleDelete();
          })();
        },
        variant: "destructive" as const,
        show: true,
      },
    ],
    [liability, actions],
  );

  const handleEditRate = useCallback(
    (rate: LiabilityInterestRateHistory) => openRatePanel("edit", rate),
    [openRatePanel],
  );

  const handleDuplicateRate = useCallback(
    (rate: LiabilityInterestRateHistory) => openRatePanel("duplicate", rate),
    [openRatePanel],
  );

  const handleDeleteRate = useCallback((rate: LiabilityInterestRateHistory) => {
    setRateDeleteError(null);
    setRateToDelete(rate);
  }, []);

  const handleEditPayment = useCallback(
    (entry: LiabilityPaymentHistoryEntry) => {
      openPanel("edit", {
        id: entry.id,
        amount: String(entry.amount),
        principalAmount: String(entry.principalAmount),
        paymentDate: new Date(entry.date),
        fromAccount:
          entry.fromAccountId === undefined
            ? null
            : {
                value: String(entry.fromAccountId),
                label: entry.fromAccountName ?? "",
              },
        note: entry.note,
      });
    },
    [openPanel],
  );

  const handleDeletePayment = useCallback(
    (entry: LiabilityPaymentHistoryEntry) => {
      actions.resetDeletePaymentError();
      setDeletePayment({
        id: entry.id,
        amount: entry.amount,
        isOpen: true,
      });
    },
    [actions, setDeletePayment],
  );

  const handleConfirmDeleteRate = useCallback(async () => {
    if (!rateToDelete) {
      return;
    }
    setRateDeleteError(null);
    try {
      await deleteInterestRateTrigger({
        id,
        interestRateId: rateToDelete.id,
      }).unwrap();
      notify.success("Interest rate deleted");
      setRateToDelete(null);
    } catch (err) {
      // Keep the modal open with an inline error so the user can retry.
      setRateDeleteError(getErrorMessages(err as ErrorResponse).join(" "));
    }
  }, [id, rateToDelete, deleteInterestRateTrigger]);

  if (isLoading && !liability.id) {
    return <LiabilitySkeleton />;
  }

  if (!isLoading && notFoundError) {
    return (
      <Wrapper>
        <SectionWrapper $isFirst>
          <EmptyState
            title="Liability not found"
            description="The liability you're looking for doesn't exist."
            action={
              <Button
                size="large"
                type={ButtonTypes.primary}
                onClick={() => setLocation("/liabilities")}
              >
                Back to liabilities
              </Button>
            }
          />
        </SectionWrapper>
      </Wrapper>
    );
  }

  if (error && !liability.id) {
    return (
      <Wrapper>
        <SectionWrapper $isFirst>
          <ErrorState message={error} onRetry={refetch} />
        </SectionWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <StatusChangeModal
        isOpen={activateModal}
        liability={liability}
        onClose={() => setActivateModal(false)}
        onConfirm={actions.handleOnStatusChangeClick}
      />
      <SectionWrapper $isFirst={true}>
        <LiabilityHero
          liability={liability}
          lastPaymentDate={lastPaymentDate}
          dropdownActions={dropdownActions}
          onLogPayment={() => openPanel("create")}
          onEditLiability={actions.handleEdit}
        />
      </SectionWrapper>

      <SectionWrapper>
        <LoanBreakdown liability={liability} />
      </SectionWrapper>

      <SectionWrapper>
        <LiabilityTrends
          id={id}
          liability={liability}
          points={balancePoints}
          isLoading={isHistoryLoading}
        />
      </SectionWrapper>

      <SectionWrapper>
        <InterestRateHistory
          liability={liability}
          onEditRate={handleEditRate}
          onDuplicateRate={handleDuplicateRate}
          onDeleteRate={handleDeleteRate}
        />
      </SectionWrapper>

      <SectionWrapper>
        <PaymentHistory
          payments={paymentHistory}
          onEditPayment={handleEditPayment}
          onDeletePayment={handleDeletePayment}
        />
      </SectionWrapper>

      {isUpdateOpen && (
        <UpdateLiability
          id={id}
          liability={liabilityUpdate}
          isOpen={isUpdateOpen}
          onClose={() => {
            setIsUpdateOpen(false);
            setLiabilityUpdate({} as LiabilityUpdateObject);
          }}
        />
      )}
      {rateMode && (
        <SetInterestRate
          id={id}
          liability={liability}
          mode={rateMode}
          rate={selectedRate}
          isOpen={Boolean(rateMode)}
          onClose={closeRatePanel}
        />
      )}
      {panel.isOpen && (
        <PaymentPanel
          title={getPanelTitle()}
          payment={panel.payment}
          liabilityId={id}
          liability={liability}
          handleOnSubmit={actions.handleSubmit}
          isOpen={panel.isOpen}
          onClose={closePanel}
        />
      )}
      <DeleteLiabilityModal
        isOpen={isDeleteOpen}
        liabilityName={liability.name}
        onClose={() => setIsDeleteOpen(false)}
        onDelete={actions.handleOnDeleteLiability}
        deletionImpact={actions.deletionImpact}
        isLoadingImpact={actions.isLoadingImpact}
        isDeleting={actions.isDeletingLiability}
        deleteError={actions.deleteLiabilityError}
      />
      <DeleteInterestRateModal
        isOpen={Boolean(rateToDelete)}
        rate={rateToDelete}
        onClose={() => setRateToDelete(null)}
        onConfirm={handleConfirmDeleteRate}
        isDeleting={isDeletingRate}
        deleteError={rateDeleteError}
      />
      <DeletePaymentModal
        isOpen={deletePayment.isOpen}
        paymentAmount={deletePayment.amount}
        onClose={() => setDeletePayment((prev) => ({ ...prev, isOpen: false }))}
        onDelete={actions.handleOnDeletePayment}
        isDeleting={actions.isDeletingPayment}
        deleteError={actions.deletePaymentError}
      />
    </Wrapper>
  );
};
